'use client'

import { useEffect, useRef } from 'react'

const DURATION_MS = 1600
const easeOutExpo = (t: number) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t))

export default function About() {
  const statsRef = useRef<HTMLDivElement>(null)
  const hasAnimated = useRef(false)

  useEffect(() => {
    const node = statsRef.current
    if (!node) return

    const reducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const animateCounters = () => {
      const counters = node.querySelectorAll<HTMLElement>('[data-stat-target]')
      counters.forEach((counter) => {
        const target = Number(counter.dataset.statTarget) || 0

        if (reducedMotion) {
          counter.textContent = `${target}+`
          return
        }

        const start = performance.now()
        const tick = (now: number) => {
          const t = Math.min(1, (now - start) / DURATION_MS)
          const value = Math.round(target * easeOutExpo(t))
          counter.textContent = `${value}+`
          if (t < 1) requestAnimationFrame(tick)
        }
        requestAnimationFrame(tick)
      })
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true
            animateCounters()
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.5 }
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return (
    <section id="about" className="about-section">
      <div className="container">
        <h2 className="about-section-title font-logo">About Us</h2>
        <p className="about-section-description">Trusted by startups and enterprises alike, we turn complex business challenges into streamlined software solutions that deliver measurable results</p>

        <div className="about-content">
          <div className="about-text">
            <h3 className="about-subtitle">Our Mission</h3>
            <p className="about-paragraph">
              We build software that solves real business problems and delivers ROI you can measure. Our clients typically see 3-5x productivity gains within the first 90 days. We are not just building technology—we are building your competitive advantage.
            </p>

            <h3 className="about-subtitle">Our Values</h3>
            <div className="values-list">
              <div className="value-item">
                <div className="value-header">
                  <h4 className="value-title">Innovation</h4>
                  <div className="value-separator"></div>
                </div>
                <p className="value-description">
                  We do not just follow trends—we set them. Our team stays ahead of the curve by constantly learning new technologies and exploring fresh approaches to problem-solving. We believe the best solutions come from thinking differently,
                  which is why we challenge conventional methods and embrace creative thinking in every project we undertake.
                </p>
              </div>
              <div className="value-item">
                <div className="value-header">
                  <h4 className="value-title">Collaboration</h4>
                  <div className="value-separator"></div>
                </div>
                <p className="value-description">
                  Great software is built by great teams. We work closely with our clients, treating them as partners throughout the entire development journey. By maintaining open lines of communication and valuing every perspective, we create
                  solutions that truly reflect your vision and needs. Together, we turn ideas into reality.
                </p>
              </div>
              <div className="value-item">
                <div className="value-header">
                  <h4 className="value-title">Quality</h4>
                  <div className="value-separator"></div>
                </div>
                <p className="value-description">
                  We take pride in our work and it shows. Every line of code, every design decision, and every feature is carefully crafted to meet our high standards. We test thoroughly, iterate continuously, and never compromise on quality. When
                  you work with us, you get software that is reliable, maintainable, and built to last.
                </p>
              </div>
            </div>
          </div>

          <div className="about-stats" ref={statsRef}>
            <div className="stat-item">
              <h3 data-stat-target="100" className="tabular">0+</h3>
              <p>Projects Completed</p>
            </div>
            <div className="stat-item">
              <h3 data-stat-target="38" className="tabular">0+</h3>
              <p>Happy Clients</p>
            </div>
            <div className="stat-item">
              <h3 data-stat-target="15" className="tabular">0+</h3>
              <p>Years Experience</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
